import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getMiddleAsRightPanelProps, getMiddlePanelProps } from './utils';
import { PANEL_CONFIGURATIONS } from '../../../constants/PanelLayoutConstants';
import { useFormSelector } from '../../../custom-hooks';
import PreventMountMessage from '../../../form-designer/components/PreventMountMessage';
import { FORM_KEYS } from '../../../form-designer/constants/form-manager.constants';
import { getContainerWidthPercentBasedOnSelectedSubGrids } from '../../../form-designer/selectors/runtime.selectors';
import { getPreventFormMounting } from '../../../selectors/AppConfigsSelectors';
import {
  getDecisionTreeIframeSrc,
  getInspectionMeasurementDataIframeSrc,
} from '../../../selectors/WorkflowSelectors';
import { getUITA } from '../../../utils/commonUtils';
import CustomGridPanel from '../CustomGridPanel/CustomGridPanel';

const useStyles = makeStyles({
  contentContainer: {
    maxHeight: 'unset',
    overflowY: 'unset',
    padding: 'unset',
  },
  contentChildren: {
    height: 'calc(100% - 64px)',
    paddingLeft: 8,
    paddingRight: (isMobileDevice) => (isMobileDevice ? 8 : 32),
    overflowY: 'auto',
  },
  contentChildrenWithIframe: {
    height: '100%',
    overflow: 'hidden',
    paddingLeft: 8,
    paddingRight: 0,

    '& iframe': {
      border: 0,
    },
    '& :not(iframe)': {
      display: 'none',
    },
  },
  formButtonGroupRootWithIframe: {
    display: 'none',
  },
  hideScrollBar: {
    marginRight: '-50px !important',
    paddingRight: '50px !important',
  },
});

function MiddlePanel({
  children,
  config,
  middlePanelFormFooter,
  setConfig,
  mountRightPanel,
  isRightPanelMounted,
  isMobileDevice,
  hideRightButton,
  invertedPanel,
  ...otherProps
}) {
  const classes = useStyles();
  const { pathname } = useLocation();
  const decisionTreeIframeSrc = useSelector(getDecisionTreeIframeSrc);
  const inspectionMeasurementDataIframeSrc = useSelector(getInspectionMeasurementDataIframeSrc);
  const preventFormMounting = useSelector(getPreventFormMounting);

  const isIframeSrcDefined = decisionTreeIframeSrc || inspectionMeasurementDataIframeSrc;
  const isHideScroll = otherProps?.panelProps?.isHideScroll;
  const panelProps = invertedPanel
    ? getMiddleAsRightPanelProps({ config, setConfig, otherProps })
    : getMiddlePanelProps({ config, setConfig, otherProps });

  const widthPercent = useFormSelector(getContainerWidthPercentBasedOnSelectedSubGrids, {
    pathname,
    formKey: FORM_KEYS.STEP,
    defaultWidthPercent: panelProps.widthPercent,
  });

  panelProps.widthPercent = widthPercent;

  if (invertedPanel) {
    otherProps.hideLeftButton = false;
    otherProps.hideRightButton = true;
  }

  if (isMobileDevice) {
    otherProps.hideButtons = true;
  }

  useEffect(() => {
    const isRightPanelOpened =
      config === PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED ||
      config === PANEL_CONFIGURATIONS.ALL_EXPANDED ||
      config === PANEL_CONFIGURATIONS.LEFT_COLLAPSED ||
      config === PANEL_CONFIGURATIONS.ONLY_RIGHT;

    if (isRightPanelOpened && !isRightPanelMounted) {
      mountRightPanel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, isRightPanelMounted]);

  const isRightPanelOpened =
    config === PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED || config === PANEL_CONFIGURATIONS.ONLY_RIGHT;

  return (
    <CustomGridPanel
      {...otherProps}
      {...panelProps}
      widthPercent={isMobileDevice && !isRightPanelOpened ? 100 : panelProps.widthPercent}
      classes={{ contentContainer: classes.contentContainer }}
      bgColor="#fff"
      testAutomation={getUITA('StepFormPanel')}
      hideRightButton={preventFormMounting || hideRightButton}
    >
      {preventFormMounting ? (
        <PreventMountMessage />
      ) : (
        <>
          <div
            className={
              isIframeSrcDefined
                ? classes.contentChildrenWithIframe
                : isHideScroll
                ? classNames(classes.contentChildren, classes.hideScrollBar)
                : classes.contentChildren
            }
            ui-ta="ScrollingElement"
          >
            {isIframeSrcDefined && (
              <iframe
                title={
                  decisionTreeIframeSrc
                    ? 'DecisionTreeIframeSrc'
                    : 'InspectionMeasurementDataIframeSrc'
                }
                src={decisionTreeIframeSrc ?? inspectionMeasurementDataIframeSrc}
                id="frameDecision"
                width="100%"
                height="100%"
                className={classes.iframe}
              />
            )}
            {children}
          </div>
          <div className={isIframeSrcDefined ? classes.formButtonGroupRootWithIframe : ''}>
            {middlePanelFormFooter}
          </div>
        </>
      )}
    </CustomGridPanel>
  );
}

export default MiddlePanel;
